/**
 * Frontend Quota Manager
 * Handles patient-months quota operations
 * Patient-months = cumulative count of unique patient+month combinations
 */
import { apiClient } from './apiClient';

export class QuotaManager {
  constructor() {
    this.useMockData = import.meta.env.MODE === 'development' && !import.meta.env.VITE_API_ENDPOINT;
  }

  /**
   * Set quota limit for a clinic (super admin only)
   * @param {string} clinicId - The clinic ID
   * @param {Object} quota - Quota settings { limit: number, effectiveDate?: string }
   * @returns {Promise<Object>} Result
   */
  async setClinicQuota(clinicId, quota) {
    if (this.useMockData) {
      console.log('Mock: Would set quota for clinic:', clinicId, quota);
      return { success: true, message: `Quota set to ${quota.limit} for clinic ${clinicId}` };
    }

    try {
      const data = await apiClient.quotas({
        operation: 'setClinicQuota',
        clinicId,
        quota
      });
      return data;
    } catch (error) {
      console.error(`Error setting quota for clinic ${clinicId}:`, error);
      throw new Error(`Failed to set quota: ${error.message}`);
    }
  }

  /**
   * Get quota settings for a clinic
   * @param {string} clinicId - The clinic ID
   * @returns {Promise<Object>} Quota settings
   */
  async getClinicQuota(clinicId) {
    if (this.useMockData) {
      return {
        success: true,
        clinicId,
        clinicName: 'Mock Clinic',
        patientQuota: { limit: 50, effectiveDate: '2024-01-01' }
      };
    }

    try {
      const data = await apiClient.quotas({
        operation: 'getClinicQuota',
        clinicId
      });
      return data;
    } catch (error) {
      console.error(`Error getting quota for clinic ${clinicId}:`, error);
      throw new Error(`Failed to get quota: ${error.message}`);
    }
  }

  /**
   * Get combined quota status (quota + patient-months usage + remaining)
   * @param {string} clinicId - The clinic ID
   * @returns {Promise<Object>} Quota status with patientMonths metric
   */
  async getClinicQuotaStatus(clinicId) {
    if (this.useMockData) {
      return {
        success: true,
        clinicId,
        clinicName: 'Mock Clinic',
        quota: { limit: 50 },
        patientMonths: 35,
        patientMonthsBreakdown: {
          '2024-01': 10,
          '2024-02': 12,
          '2024-03': 13
        },
        remaining: 15,
        utilizationPercent: 70,
        quotaStatus: 'ok'
      };
    }

    try {
      const data = await apiClient.quotas({
        operation: 'getClinicQuotaStatus',
        clinicId
      });
      return data;
    } catch (error) {
      console.error(`Error getting quota status for clinic ${clinicId}:`, error);
      throw new Error(`Failed to get quota status: ${error.message}`);
    }
  }

  /**
   * Get all clinics quota status (super admin only)
   * @returns {Promise<Object>} All quota statuses with patientMonths metric
   */
  async getAllQuotaStatuses() {
    if (this.useMockData) {
      return {
        success: true,
        clinics: [
          {
            clinicId: 'clinic-1',
            clinicName: 'Israel Pilot',
            quota: { limit: 50 },
            patientMonths: 35,
            patientMonthsBreakdown: { '2024-01': 10, '2024-02': 12, '2024-03': 13 },
            remaining: 15,
            utilizationPercent: 70,
            quotaStatus: 'ok'
          },
          {
            clinicId: 'clinic-2',
            clinicName: 'US Trial',
            quota: { limit: 30 },
            patientMonths: 28,
            patientMonthsBreakdown: { '2024-01': 8, '2024-02': 10, '2024-03': 10 },
            remaining: 2,
            utilizationPercent: 93,
            quotaStatus: 'warning'
          }
        ]
      };
    }

    try {
      const data = await apiClient.quotas({
        operation: 'getAllQuotaStatuses'
      });
      return data;
    } catch (error) {
      console.error('Error getting all quota statuses:', error);
      throw new Error(`Failed to get quota statuses: ${error.message}`);
    }
  }

  /**
   * Set email notification list for a clinic (super admin only)
   * @param {string} clinicId - The clinic ID
   * @param {string} emailList - Comma-separated email addresses
   * @returns {Promise<Object>} Result
   */
  async setClinicEmailNotificationList(clinicId, emailList) {
    if (this.useMockData) {
      console.log('Mock: Would set email list for clinic:', clinicId, emailList);
      return { success: true, message: 'Email notification list updated' };
    }

    try {
      const data = await apiClient.quotas({
        operation: 'setClinicEmailNotificationList',
        clinicId,
        emailList
      });
      return data;
    } catch (error) {
      console.error(`Error setting email list for clinic ${clinicId}:`, error);
      throw new Error(`Failed to set email list: ${error.message}`);
    }
  }

  /**
   * Get quota status display color
   * @param {string} quotaStatus - The quota status ('ok', 'warning', 'exceeded', 'no_quota')
   * @returns {string} CSS color class
   */
  getStatusColor(quotaStatus) {
    switch (quotaStatus) {
      case 'ok':
        return 'success';
      case 'warning':
        return 'warning';
      case 'exceeded':
        return 'error';
      case 'no_quota':
      default:
        return 'default';
    }
  }

  /**
   * Format quota status for display
   * @param {Object} status - The quota status object
   * @returns {string} Formatted status text
   */
  formatQuotaStatus(status) {
    if (status.quotaStatus === 'no_quota') {
      return 'No quota set';
    }

    const { patientMonths, quota, remaining, utilizationPercent } = status;
    return `${patientMonths}/${quota?.limit || 0} Credits (${utilizationPercent}% used, ${remaining} remaining)`;
  }
}

// Export singleton instance
export const quotaManager = new QuotaManager();
